import { Box, Button, FormControl, InputAdornment, TextField } from "@mui/material";
import { InputLabel } from "components/common/Input";
import { Formik } from "formik";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "services/api.service";

type TransactionGroup = {
  quickbooksId: string;
  earningsCredit: string;
  postedAt: Date;
  orderNumber: string;
};

export function ClaimedInsuranceTransactionGroupForm() {
  const { uuid, transactionGroupId } = useParams();
  const [today] = useState(() => new Date());
  const navigate = useNavigate();
  const [transactionGroup, setTransactionGroup] = useState<TransactionGroup | null>(null);

  useEffect(() => {
    if (!transactionGroupId) return;
    api.get(`/organizations/${uuid}/transaction_groups/${transactionGroupId}`).then(({ data }) => {
      setTransactionGroup({
        quickbooksId: data.quickbooksId ?? data.quickbooks_id ?? "",
        earningsCredit: String(data.earningsCredit ?? data.earnings_credit ?? ""),
        postedAt: data.postedAt ?? data.posted_at ?? new Date(),
        orderNumber: String(data.orderNumber ?? data.order_number ?? ""),
      });
    });
  }, [transactionGroupId, uuid]);

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={
          transactionGroup || {
            quickbooksId: "",
            earningsCredit: "",
            postedAt: today,
            orderNumber: "",
          }
        }
        onSubmit={(values) => {
          const payload: Record<string, unknown> = {
            type: "ClaimedInsurance",
            quickbooksId: values.quickbooksId,
            earningsCredit: values.earningsCredit,
            postedAt: values.postedAt,
          };
          if (values.orderNumber?.trim()) {
            payload.orderNumber = values.orderNumber.trim();
          }
          api
            .post(`/organizations/${uuid}/transaction_groups`, {
              transactionGroup: payload,
            })
            .then(({ data }) => {
              navigate(`/finance/clients/${uuid}/transaction_groups/ClaimedInsurance/${data.id}`, {
                replace: true,
              });
            });
        }}
      >
        {({ getFieldProps, handleSubmit }) => (
          <Box
            sx={{
              p: 4,
              mb: 4,
              background: "background.paper",
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink>Credit Amount:</InputLabel>
                <TextField
                  size="small"
                  {...getFieldProps("earningsCredit")}
                  disabled={!!transactionGroupId}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </FormControl>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink>Retrieval Order Number:</InputLabel>
                <TextField
                  size="small"
                  {...getFieldProps("orderNumber")}
                  disabled={!!transactionGroupId}
                  placeholder="e.g. AB12CD34"
                />
              </FormControl>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink>Posted At:</InputLabel>
                <TextField
                  id="postedAt"
                  size="small"
                  type={transactionGroupId ? "" : "date"}
                  {...getFieldProps("postedAt")}
                  disabled={!!transactionGroupId}
                />
              </FormControl>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink>Quickbooks ID:</InputLabel>
                <TextField size="small" {...getFieldProps("quickbooksId")} disabled={!!transactionGroupId} />
              </FormControl>
            </Box>
            {!transactionGroupId && (
              <Box display="flex" justifyContent="flex-end" mt={6}>
                <Button variant="contained" onClick={() => handleSubmit()}>
                  Save
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Formik>
    </>
  );
}
